import { Component, ChangeDetectionStrategy, inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { map, Observable, combineLatest, firstValueFrom, BehaviorSubject } from 'rxjs';
import { take, takeUntil, filter, distinctUntilChanged } from 'rxjs/operators';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Button } from 'ustore-ui';
import { Subject } from 'rxjs';

import { CartFacade } from '../../../core/store/cart/cart.facade';
import { CartItem } from '../../../core/store/cart/cart.state';
import { CartPlan, CartProduct } from '../../../core/models/cart.model';
import { CartProductListComponent } from '../cart-product-list/cart-product-list.component';
import { LoadingComponent } from '../loading/loading.component';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { CartService } from '../../services/cart.service';
import { ToastService } from '../../services/toast.service';
import { GtmService, Ga4Item } from '../../../core/services/gtm.service';
import { CategoryIconMapperService } from '../../services/category-icon-mapper.service';
import { UserFacade } from '../../../core/store/user/user.facade';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { ProductService } from '../../../core/services/product.service';
import { ProductCategory } from '../../../core/models/product.model';
import { UManagerSlugService, ChannelData } from '../../../core/services/umanager-slug.service';
import { UtmPreservationService } from '../../../core/services/utm-preservation.service';
import { UtmCnpjAccountService } from '../../../core/services/utm-cnpj-account.service';
import { ChannelThemeService } from '../../../core/services/channel-theme.service';
import { ChannelConfigService } from '../../../core/services/channel-config.service';
import { MindCatalogStore } from '../../../core/store/mind/mind-catalog.store';
import { LocationFacade } from '../../../core/store/location/location.facade';
import { CategoryHelperService } from '../../services/category-helper.service';
import { getMindPromoUiFromProduct } from '../../../core/utils/mind-price.utils';
import { isOneTimeRegistryDomainPrice } from '../../../core/utils/cart-domain-suggestion-rules';

@Component({
  selector: 'umkt-cart-page',
  standalone: true,
  imports: [CommonModule, CartProductListComponent, Button, LoadingComponent, ConfirmModalComponent, I18nPipe],
  templateUrl: './cart-page.component.html',
  styleUrl: './cart-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class CartPageComponent implements OnInit, OnDestroy {
  private cart = inject(CartFacade);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toast = inject(ToastService);
  private cartService = inject(CartService);
  private gtm = inject(GtmService);
  private categoryMapper = inject(CategoryIconMapperService);
  private userFacade = inject(UserFacade);
  private i18nService = inject(I18nService); 
  private productService = inject(ProductService);
  private umanagerSlugService = inject(UManagerSlugService);
  private utmPreservationService = inject(UtmPreservationService);
  private utmCnpjAccountService = inject(UtmCnpjAccountService);
  private locationFacade = inject(LocationFacade);
  private destroy$ = new Subject<void>();
  private previousUtmSource: string | null = null;
  private channelBrandService = inject(ChannelConfigService);
  private mindStore = inject(MindCatalogStore);

  items$ = this.cart.getCartItems();
  count$ = this.cart.getCartItemCount();
  loading$ = this.cart.getIsLoading();
  urlCartLoading$ = this.cart.getUrlCartLoading();

  combinedLoading$ = combineLatest([
    this.loading$,
    this.urlCartLoading$
  ]).pipe(
    map(([cartLoading, urlCartLoading]) => cartLoading || urlCartLoading)
  );
  error$ = this.cart.getError();
  subtotal$ = this.cart.getCartSubtotal();

  showClearConfirm = false;

  mockCartProducts$ = new BehaviorSubject<CartProduct[]>([]);
  private channelData: ChannelData | null = null;
  private platformId = inject(PLATFORM_ID);
  private channelThemeService = inject(ChannelThemeService);
  private categoryHelper = inject(CategoryHelperService);


  ngOnInit(): void {
    this.checkAndLoadChannelData().then(() => {
      this.loadCategoriesByCatalog();
      this.previousUtmSource = this.getCurrentUtmSource();
    });
    this.i18nService.getCurrentLanguage().pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.productService.clearCategoriesCache();
      setTimeout(() => {
        this.loadCategoriesByCatalog();
      });
    });

    this.mindStore.catalog$
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.loadCategoriesByCatalog();
    });

    // Listen to query params changes to detect UTM removal/addition
    this.route.queryParams.pipe(
      distinctUntilChanged((prev, curr) => {
        const prevUtm = prev['utm_source'] || null;
        const currUtm = curr['utm_source'] || null;
        return prevUtm === currUtm;
      }),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.handleUtmChange();
    });

    // Also listen to navigation events to catch cases where UTM might be removed from preserved params
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Small delay to ensure query params are updated
      setTimeout(() => {
        this.handleUtmChange();
      }, 100);
    });

    combineLatest([
    this.items$,
    this.route.queryParams
    ])
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe(([items]) => {
      const utmSource = this.getCurrentUtmSource();

      if (!utmSource) {
        const hasMindItems = items.some(it => it.product?.productType === 'MIND');

        if (hasMindItems) {
          this.cart.clearCart();
          console.warn('Slug removido — limpando produtos.');
        }
      }
    });
  }

  private handleUtmChange(): void {
    const currentUtmSource = this.getCurrentUtmSource();
    
    // Only reload if UTM actually changed
    if (currentUtmSource !== this.previousUtmSource) {
      // If UTM was removed, clear channel data
      if (this.previousUtmSource && !currentUtmSource) {
        this.channelData = null;
      }
      
      this.checkAndLoadChannelData().then(() => {
        this.loadCategoriesByCatalog();
      });
      
      this.previousUtmSource = currentUtmSource;
    }
  }

  private getCurrentUtmSource(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const utmSourceFromUrl = urlParams.get('utm_source');
    
    if (utmSourceFromUrl) {
      return utmSourceFromUrl;
    }
    
    return this.utmPreservationService.preservedUtmParams?.['utm_source'] || null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkAndLoadChannelData(): Promise<void> {
    return new Promise((resolve) => {
      if (!isPlatformBrowser(this.platformId)) {
        this.channelData = null;
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      const utmSource = this.getCurrentUtmSource();

      if (!utmSource) {
        this.channelData = null;
        this.channelThemeService.resetToDefault();
        this.channelBrandService.setChannelData(null);
        resolve();
        return;
      }

      this.umanagerSlugService.checkSlugExists(utmSource).pipe(
        takeUntil(this.destroy$),
        take(1)
      ).subscribe({
        next: (response) => {
          if (response?.exists && utmSource) {
            this.umanagerSlugService.getChannelBySlug(utmSource).pipe(
              takeUntil(this.destroy$),
              take(1)
            ).subscribe({
              next: (channelData) => {
                if (channelData?.status && channelData.status !== 'ACTIVE') {
                  this.channelData = null;
                  this.utmPreservationService.disableUtmAndCleanUrl();
                  this.channelThemeService.resetToDefault();
                  this.channelBrandService.setChannelData(null);
                  resolve();
                  return;
                }

                this.channelData = channelData || null;
                const parsedColors = this.channelThemeService.parseChannelColors(
                  channelData?.colors ?? null
                );
                if (parsedColors) {
                  this.channelThemeService.applyChannelColors(parsedColors);
                } else {
                  this.channelThemeService.resetToDefault();
                }
                this.channelBrandService.setChannelData(channelData);
                resolve();
              },
              error: (error) => {
                console.error('Error loading channel data:', error);
                resolve();
              }
            });
          } else {
            this.channelData = null;
            this.channelBrandService.setChannelData(null);
            resolve();
          }
        },
        error: (error) => {
          console.error('Error checking slug existence:', error);
          resolve();
        }
      });
    });
  }

  private getChannelCatalogs(): string[] {
    if (!this.channelData?.catalogs) return [];
    return this.channelData.catalogs.map((c: string) => c.toUpperCase());
  }

  private loadCategoriesByCatalog(): void {
    const catalogs = this.channelData?.catalogs?.length
    ? this.getChannelCatalogs()
    : [];

    const hasSva = catalogs.includes('SVA');
    const hasMind = catalogs.includes('MIND');

    if (!this.channelData || catalogs.length === 0) {
      this.loadCategoriesForEmptyCart();
      return;
    }

    if (hasSva && hasMind) {
      this.getCategoriesCombined();
      return;
    }

    if (hasSva) {
      this.loadCategoriesForEmptyCart();
      return;
    }

    if (hasMind) {
      this.getCategoriesMind();
      return;
    }
  }

  getCategoriesCombined(): void {
    this.productService.getCategories().pipe(take(1)).subscribe({
      next: resp => {
        let svaCategories = resp.data;
        if (this.channelData?.categoriesUuid?.length) {
          svaCategories = svaCategories.filter(cat =>
            this.channelData!.categoriesUuid.includes(cat.uuid)
          );
        }

        const catalog = this.mindStore.snapshot;
        const mindCategories = (catalog?.groups ?? [])
          .filter(group => group.items && group.items.length > 0) 
          .map(group => ({
            name: group.title,
            uuid: `${group.groupId}`,
            hasProducts: true
          }));

        const merged = [...svaCategories, ...mindCategories];

        this.mockCartProducts$.next(
          this.convertCategoriesToCartProducts(merged as any)
        );
      },
      error: () => this.mockCartProducts$.next([])
    });
  }

  private getCategoriesMind(): void {
    this.mindStore.catalog$
      .pipe(
        filter(catalog => !!catalog && !!catalog.groups),
        take(1)
      )
      .subscribe(catalog => {

        const mindCategories = catalog?.groups
          .filter(group => group.items && group.items.length > 0) 
          .map(group => ({
            name: group.title,
            uuid: `${group.groupId}`,
            hasProducts: true
          }));

        this.mockCartProducts$.next(
          this.convertCategoriesToCartProducts(mindCategories as any)
        );
      });
  }
  private loadCategoriesForEmptyCart(): void {
    this.productService.getCategories().pipe(
      takeUntil(this.destroy$),
      take(1)
    ).subscribe({
      next: (resp) => {

        const categories =
          this.categoryHelper.filterAndSortCategories(
            resp.data,
            this.channelData?.categoriesUuid
          );

        const cartProducts = this.convertCategoriesToCartProducts(categories);

        this.mockCartProducts$.next(cartProducts);
      },
      error: (error) => {
        console.error('Error loading categories for empty cart:', error);
        this.mockCartProducts$.next([]);
      }
    });
  }

  private convertCategoriesToCartProducts(categories: ProductCategory[]): CartProduct[] {
    return categories.map(category => {
      const iconCategory =
        this.categoryMapper.mapCategoryUuidToIconCategory(category.uuid);

      return {
        id: category.uuid,
        categories: [iconCategory],
        name: category.name,
        subtotal: 0,
        plans: [],
        termsUrl: null
      };
    });
  }

  products$: Observable<CartProduct[]> = this.items$.pipe(
    map(items => this.mapItemsToProducts(items))
  );

  onRemovePlan(ev: { productId: string; planId: string }) {
    this.items$.pipe(take(1)).subscribe(items => {
      const found = items.find(it => {
        const isMind = it.product?.productType === 'MIND';
        if (isMind) {
          return String(it.product?.uuid) === ev.productId;
        }
        return String(it.product?.uuid) === ev.productId && it.selectedVariation?.uuid === ev.planId;
      });

      if (found) {
        this.cart.removeFromCart(found.id);
      }
    });
  }

  openClearCartConfirm() {
    this.showClearConfirm = true;
  }

  handleClearConfirm() {
    this.showClearConfirm = false;
    this.cart.clearCart({ emitEvent: true });
    this.toast.showSuccess(
      this.i18nService.instant('components.cart.messages.clearSuccess')
    );
  }

  handleClearCancelOrClose() {
    this.showClearConfirm = false;
  }

  private mapItemsToProducts(items: CartItem[]): CartProduct[] {
    interface Group {
      name: string;
      categories: string[];
      rows: CartItem[];
      termsUrl: string | null;
      productType?: string;
    }

    const byProduct = new Map<string, Group>();

    for (const it of items ?? []) {
      if (!it.product?.uuid) continue;

      const key = it.product.uuid;
      const finalCategory = this.categoryMapper.mapProductToIconCategory(
        it.product.categories || [],
        it.product.productType
      );

      const categories = [finalCategory];
      const name = it.product.name ?? 'Produto';
      const termsUrl = it.product.termsUrl ?? null;

      let group = byProduct.get(key);
      if (!group) {
        group = { name, categories, rows: [], termsUrl, productType: it.product.productType };
        byProduct.set(key, group);
      }

      group.rows.push(it);
    }

    const products: CartProduct[] = [];
    byProduct.forEach((group, key) => {
      const plans: CartPlan[] = group.rows.map(row => {
        const isMind = row.product?.productType === 'MIND';
        const v = row.selectedVariation;
        const planId = isMind || !v ? row.product.uuid : v.uuid;

        if (isMind || !v) {
          const mindPromo = isMind
            ? getMindPromoUiFromProduct(row.product)
            : null;
          return {
            id: planId,
            name: row.product.name,
            unitPrice:
              mindPromo?.displayPrice ??
              row.product.salesPrice ??
              row.product.price ??
              0,
            originalUnitPrice: mindPromo?.hasPromotion
              ? mindPromo.originalPrice
              : undefined,
            hasPromotion: mindPromo?.hasPromotion ?? false,
            promotionMonths: mindPromo?.promotionMonths,
            paymentRecurrence:
              row.product.paymentRecurrence ?? 'monthly',
            quantity: row.quantity ?? 1,
            addons: [],
          };
        }

        return {
          id: planId,
          name: v.name ?? row.product.name,
          unitPrice:
            v.salesPrice ??
            v.price ??
            row.product.salesPrice ??
            row.product.price ??
            0,
          paymentRecurrence: v.paymentRecurrence,
          quantity: row.quantity ?? 1,
          addons: [],
        };
      });

      if (plans.length === 0) return;

      const subtotal = plans.reduce((acc, p) => {
        const adjustedPrice = isOneTimeRegistryDomainPrice(key, p.id)
          ? p.unitPrice
          : p.paymentRecurrence === 'annual'
            ? p.unitPrice / 12
            : p.unitPrice;
        return acc + adjustedPrice * (p.quantity ?? 1);
      }, 0);

      products.push({
        id: key,
        name: group.name,
        categories: group.categories,
        subtotal,
        plans,
        termsUrl: group.termsUrl,
        productType: group.productType
      });
    });

    return products;
  }

  onChangeQty(ev: { productId: string; planId: string; quantity: number }) {
  this.items$.pipe(take(1)).subscribe(items => {
    const found = items.find(it =>
      String(it.product?.uuid) === ev.productId &&
      it.selectedVariation?.uuid === ev.planId
    );
    if (!found) return;

    if (ev.quantity > 0) {
      this.cart.updateCartItemQuantity(found.id, ev.quantity);
      const _item: Ga4Item = {
        item_id: found.product.sku || found.product.uuid || found.product.id,
        item_name: found.product.name,
        item_category: found.product.categories?.[0]?.name,
        item_variant: found.selectedVariation?.name,
        price: found.selectedVariation?.salesPrice ?? found.selectedVariation?.price ?? found.product.salesPrice ?? found.product.price ?? 0,
        quantity: ev.quantity
      };
    } else {
      this.cart.removeFromCart(found.id);
      const item: Ga4Item = {
        item_id: found.product.sku || found.product.uuid || found.product.id,
        item_name: found.product.name,
        item_category: found.product.categories?.[0]?.name,
        item_variant: found.selectedVariation?.name,
        price: found.selectedVariation?.salesPrice ?? found.selectedVariation?.price ?? found.product.salesPrice ?? found.product.price ?? 0,
        quantity: found.quantity
      };
      this.gtm.removeFromCart([item]);
    }
  });
}

  onPrimaryCTA(): void {
    this.count$.pipe(take(1)).subscribe(count => {
      if (count > 0) {
        this.proceedToCheckout();
      } else {
        this.goToOffers();
      }
    });
  }

  async proceedToCheckout(): Promise<void> {

    const isAuthenticated = await firstValueFrom(this.userFacade.getIsAuthenticated());
    
    if (!isAuthenticated) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('redirect_after_login', '/checkout');
      }
      this.cartService.closeCartSidebar();

      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
        ...(this.route.snapshot?.queryParams || {}),
      });

      if (this.utmPreservationService.isUtmEnabled()) {
        const hasAccount = this.utmCnpjAccountService.getCnpjHasAccount();
        if (hasAccount === false) {
          const cnpj = this.utmCnpjAccountService.getStoredCnpj();
          const location = await firstValueFrom(this.locationFacade.location$);
          const cep = location?.cep?.replace(/\D/g, '');
          const numero = location?.addressNumber?.trim();
          const registerParams = {
            ...queryParams,
            ...(cnpj ? { cnpj } : {}),
            ...(cep && cep.length === 8 ? { cep } : {}),
            ...(numero ? { numero } : {})
          };
          this.router.navigate(['/register'], { queryParams: registerParams });
          return;
        }
      }
      this.router.navigate(['/login'], { queryParams });
      return;
    }

    const currentUser = await firstValueFrom(this.userFacade.getUserDetails());
    if (!currentUser || !currentUser.document) {
      this.toast.showError(
        this.i18nService.instant('components.cart.messages.cnpjRequired'),
        5000,
        'top-center'
      );
      this.cartService.closeCartSidebar();
      this.router.navigate(['/register-movel'], {
        queryParams: this.utmPreservationService.addPreservedUtmToQueryParams({ returnUrl: '/checkout' })
      });
      return;
    }

    this.items$.pipe(take(1)).subscribe(items => {
      const gaItems: Ga4Item[] = (items || []).map(it => ({
        item_id: it.product?.sku || it.product?.uuid || it.product?.id,
        item_name: it.product?.name,
        item_category: it.product?.categories?.[0]?.name,
        item_variant: it.selectedVariation?.name,
        price: it.selectedVariation?.salesPrice ?? it.selectedVariation?.price ?? it.product?.salesPrice ?? it.product?.price ?? 0,
        quantity: it.quantity
      }));
      const value = gaItems.reduce((acc, i) => acc + (i.price ?? 0) * (i.quantity ?? 1), 0);
      this.gtm.beginCheckout(gaItems, value);
    });
    this.router.navigate(['/checkout']);
    this.cartService.closeCartSidebar();
  }

  handleUndoRemove(ev: { productId: string; planId: string }) {
    this.items$.pipe(take(1)).subscribe(items => {
      const found = items.find(it =>
        String(it.product?.uuid) === ev.productId &&
        it.selectedVariation?.uuid === ev.planId
      );
      if (!found) return;
      this.cart.addToCart(found.product, found.quantity, found.selectedVariation);
    });
  }

  goToOffers(): void {
    // this.router.navigate(['/offers']);
    this.cartService.closeCartSidebar();
  }

}
